/**
 * WritingCanvas - Tela de Escrita Imersiva
 * 
 * REPLICAÇÃO EXATA DO MODELO:
 * - Fundo: taupe/bege neutro com textura de linho
 * - Vinheta: circular/oval, transição suave para marrom escuro
 * - Páginas: 5+ camadas, offset sutil (2-4px)
 * - Sombra: luz vindo do topo-esquerda
 * - Blur: nas linhas inferiores (depth of field)
 * - Cursor: pena de tinteiro com ponta bifurcada
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';

// Font configurations
export const WRITING_FONTS = {
  playfair: {
    name: 'Playfair Display',
    family: '"Playfair Display", serif',
    label: 'Playfair Display',
  },
  cormorant: {
    name: 'Cormorant Garamond',
    family: '"Cormorant Garamond", serif',
    label: 'Cormorant Garamond',
  },
  baskerville: {
    name: 'Libre Baskerville',
    family: '"Libre Baskerville", serif',
    label: 'Libre Baskerville',
  },
};

// Theme - cores EXATAS do modelo
const THEMES = {
  sepia: {
    // Fundo - taupe/bege neutro com tom acinzentado (como linho)
    deskColor: '#b5a898',
    // Vinheta - marrom escuro/taupe profundo
    vignetteColor: '#5a4a3a',
    vignetteIntensity: 0.5,
    // Página - off-white cremoso (papel envelhecido)
    pageColor: '#f9f6f0',
    pageEdgeColor: '#f0ebe2',
    // Texto - cinza escuro/carvão (não preto puro)
    textColor: '#3a3a3a',
    mutedColor: '#8a8578',
    placeholderColor: '#a09890',
    // Ornamento
    ornamentColor: '#c8c0b0',
  },
  dark: {
    deskColor: '#1a1816',
    vignetteColor: '#000000',
    vignetteIntensity: 0.6,
    pageColor: '#2a2725',
    pageEdgeColor: '#232120',
    textColor: '#E6E1DA',
    mutedColor: '#8a8580',
    placeholderColor: '#6b6560',
    ornamentColor: '#4a4540',
  },
};

// Cursor de pena tinteiro (fountain pen nib) - preto, ponta bifurcada
const PEN_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M3 21l3-3 L18 6c1-1 2.5-1 3.5 0s1 2.5 0 3.5L9 21l-6 0z' fill='%231a1a1a'/%3E%3Cpath d='M18 6l1.5-1.5' stroke='%231a1a1a' stroke-width='0.5'/%3E%3Cpath d='M12 12l-3 9' stroke='%231a1a1a' stroke-width='0.3' opacity='0.5'/%3E%3C/svg%3E") 3 21, auto`;

export default function WritingCanvas({
  content = '',
  title = '',
  chapterOrder = null,
  onContentChange,
  onTitleChange,
  wordCount = 0,
  theme = 'sepia',
  font = 'playfair',
  fontSize = 18,
  typewriterMode = true,
  onEditorReady,
  deepFocusMode = false,
}) {
  const editorContainerRef = useRef(null);
  const titleRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const previousContentRef = useRef(content); // Track content changes
  
  const currentTheme = THEMES[theme] || THEMES.sepia;
  const currentFont = WRITING_FONTS[font] || WRITING_FONTS.playfair;

  // Auto-resize título quando muda externamente
  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.style.height = 'auto';
      titleRef.current.style.height = titleRef.current.scrollHeight + 'px';
    }
  }, [title]);

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Placeholder.configure({
        placeholder: 'Comece a escrever sua história...',
      }),
    ],
    content,
    editorProps: {
      attributes: {
        class: 'writing-editor focus:outline-none',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText();
      onContentChange?.(html, text);
      
      setIsTyping(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 1500);

      if (typewriterMode) {
        requestAnimationFrame(() => scrollToCursor(editor));
      }
    },
  });

  useEffect(() => {
    if (editor) onEditorReady?.(editor);
  }, [editor, onEditorReady]);

  // Update editor content when prop changes (e.g., switching chapters)
  useEffect(() => {
    if (editor && content !== undefined) {
      // Only update if content actually changed (different chapter)
      if (previousContentRef.current !== content) {
        editor.commands.setContent(content || '');
        previousContentRef.current = content;
      }
    }
  }, [editor, content]);

  const scrollToCursor = useCallback((editorInstance) => {
    if (!editorContainerRef.current || !editorInstance) return;
    const { view } = editorInstance;
    const { selection } = view.state;
    
    try {
      const cursorCoords = view.coordsAtPos(selection.head);
      const container = editorContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const targetY = containerRect.height * 0.4;
      const currentY = cursorCoords.top - containerRect.top;
      const scrollOffset = currentY - targetY;
      
      if (Math.abs(scrollOffset) > 50) {
        container.scrollBy({ top: scrollOffset, behavior: 'smooth' });
      }
    } catch (e) {
      // Scroll adjustment failed - non-critical, continue silently
      console.debug('Scroll adjustment skipped:', e?.message);
    }
  }, []);

  if (!editor) {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center"
        style={{ backgroundColor: currentTheme.deskColor }}
      >
        <p style={{ color: currentTheme.mutedColor }} className="font-serif italic">
          Preparando o manuscrito...
        </p>
      </div>
    );
  }

  return (
    <div className="writing-canvas-root fixed inset-0 overflow-hidden">
      
      {/* ═══════════════════════════════════════════════════════════
          CAMADA 1: FUNDO - Taupe/bege com textura de linho
      ═══════════════════════════════════════════════════════════ */}
      <div 
        className="absolute inset-0 z-0"
        style={{ backgroundColor: currentTheme.deskColor }}
      >
        {/* Textura de linho - padrão de trama fina */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000' fill-opacity='0.15'%3E%3Cpath d='M0 0h1v1H0zM2 0h1v1H2zM4 0h1v1H4zM1 1h1v1H1zM3 1h1v1H3zM5 1h1v1H5zM0 2h1v1H0zM2 2h1v1H2zM4 2h1v1H4zM1 3h1v1H1zM3 3h1v1H3zM5 3h1v1H5zM0 4h1v1H0zM2 4h1v1H2zM4 4h1v1H4zM1 5h1v1H1zM3 5h1v1H3zM5 5h1v1H5z'/%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        {/* Vinheta oval/circular - transição suave */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse 75% 65% at 50% 45%, transparent 0%, transparent 40%, ${currentTheme.vignetteColor}${Math.round(currentTheme.vignetteIntensity * 255).toString(16).padStart(2, '0')} 100%)`,
          }}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════
          CAMADA 2: Container da página (scrollável)
      ═══════════════════════════════════════════════════════════ */}
      <div 
        ref={editorContainerRef}
        className="absolute inset-0 z-10 overflow-y-auto overflow-x-hidden"
        style={{ 
          cursor: isTyping ? 'text' : PEN_CURSOR,
          scrollbarWidth: 'none',
        }}
      >
        <style>{`
          .writing-canvas-root ::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        
        <div className="flex justify-center pt-8 md:pt-12 pb-32 px-4 min-h-full">
          <div className="relative w-full max-w-[580px]">
            
            {/* ═══════════════════════════════════════════════════════
                PÁGINAS EMPILHADAS - 5 camadas, offset sutil (2-4px)
            ═══════════════════════════════════════════════════════ */}
            
            {/* Página 5 (mais atrás) */}
            <div 
              className="absolute rounded-[2px]"
              style={{
                backgroundColor: currentTheme.pageEdgeColor,
                left: '10px',
                right: '-10px',
                top: '-10px',
                bottom: '10px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}
            />
            
            {/* Página 4 */}
            <div 
              className="absolute rounded-[2px]"
              style={{
                backgroundColor: currentTheme.pageEdgeColor,
                left: '8px',
                right: '-8px',
                top: '-8px',
                bottom: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}
            />
            
            {/* Página 3 */}
            <div 
              className="absolute rounded-[2px]"
              style={{
                backgroundColor: currentTheme.pageColor,
                left: '6px',
                right: '-6px',
                top: '-6px',
                bottom: '6px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}
            />
            
            {/* Página 2 */}
            <div 
              className="absolute rounded-[2px]"
              style={{
                backgroundColor: currentTheme.pageColor,
                left: '3px',
                right: '-3px',
                top: '-3px',
                bottom: '3px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
              }}
            />

            {/* ═══════════════════════════════════════════════════════
                PÁGINA PRINCIPAL - sombra vindo do topo-esquerda
            ═══════════════════════════════════════════════════════ */}
            <div 
              className="relative rounded-[2px] min-h-[85vh]"
              style={{
                backgroundColor: currentTheme.pageColor,
                boxShadow: `
                  2px 4px 8px rgba(0,0,0,0.1),
                  4px 8px 16px rgba(0,0,0,0.08),
                  8px 16px 32px rgba(0,0,0,0.06)
                `,
              }}
              data-testid="writing-page"
            >
              
              {/* Área de conteúdo */}
              <div 
                className="relative px-12 md:px-16 py-10 md:py-12"
                data-testid="writing-area"
              >
                
                {/* Cabeçalho */}
                <header className="mb-8 md:mb-10 text-center">
                  {/* CAPITULO 1 - pequeno, sans-serif */}
                  <p
                    className="text-[11px] uppercase tracking-[0.35em] mb-3 font-sans"
                    style={{ color: currentTheme.mutedColor }}
                  >
                    {chapterOrder ? `Capítulo ${chapterOrder}` : 'Capítulo 1'}
                  </p>
                  
                  {/* Título - serif, maior, suporta múltiplas linhas */}
                  <textarea
                    ref={titleRef}
                    value={title}
                    onChange={(e) => onTitleChange?.(e.target.value)}
                    rows={1}
                    className="w-full text-center text-[22px] md:text-[26px] font-normal bg-transparent border-0 focus:outline-none focus:ring-0 tracking-wide resize-none overflow-hidden"
                    style={{ 
                      color: currentTheme.textColor,
                      fontFamily: currentFont.family,
                      lineHeight: 1.3,
                      minHeight: '1.3em',
                    }}
                    placeholder="Título do Capítulo"
                    data-testid="chapter-title-input"
                    onInput={(e) => {
                      // Auto-resize para caber o conteúdo
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                  />
                  
                  {/* Ornamento - linha horizontal simples com símbolo sutil */}
                  <div className="flex items-center justify-center gap-3 mt-4">
                    <div 
                      className="w-14 h-[1px]"
                      style={{ backgroundColor: currentTheme.ornamentColor }}
                    />
                    <div 
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: currentTheme.ornamentColor }}
                    />
                    <div 
                      className="w-14 h-[1px]"
                      style={{ backgroundColor: currentTheme.ornamentColor }}
                    />
                  </div>
                </header>

                {/* Editor de texto */}
                <div 
                  className="writing-editor-wrapper"
                  data-testid="editor-content"
                >
                  <style>{`
                    /* Tipografia editorial - tamanho controlável */
                    .writing-editor {
                      font-family: ${currentFont.family};
                      font-size: ${fontSize}px;
                      line-height: ${1.7 + (fontSize - 16) * 0.01};
                      color: ${currentTheme.textColor};
                      background: transparent !important;
                      min-height: 400px;
                      transition: font-size 0.2s ease;
                    }
                    
                    .writing-editor .ProseMirror {
                      background: transparent !important;
                      outline: none;
                      cursor: inherit;
                    }
                    
                    /* Parágrafos - justificado */
                    .writing-editor p {
                      margin-bottom: 0;
                      text-align: justify;
                      text-indent: 1.5em;
                    }
                    
                    .writing-editor > .ProseMirror > p:first-child {
                      text-indent: 0;
                    }
                    
                    .writing-editor p + p {
                      margin-top: 0;
                    }
                    
                    /* Placeholder */
                    .writing-editor p.is-editor-empty:first-child::before {
                      content: attr(data-placeholder);
                      float: left;
                      color: ${currentTheme.placeholderColor};
                      pointer-events: none;
                      height: 0;
                      font-style: italic;
                      text-indent: 0;
                    }
                    
                    /* Headings */
                    .writing-editor h1,
                    .writing-editor h2,
                    .writing-editor h3 {
                      font-family: ${currentFont.family};
                      font-weight: 500;
                      text-align: center;
                      text-indent: 0;
                      margin-top: 2em;
                      margin-bottom: 1em;
                    }
                    
                    /* Seleção */
                    .writing-editor ::selection {
                      background: ${currentTheme.mutedColor}30;
                    }
                  `}</style>
                  
                  <EditorContent editor={editor} />
                </div>
              </div>

              {/* Contador de palavras */}
              <footer className="absolute bottom-4 left-0 right-0 text-center">
                <span
                  className="text-[10px] tracking-wider"
                  style={{ color: currentTheme.mutedColor }}
                >
                  {wordCount.toLocaleString('pt-BR')} palavras
                </span>
              </footer>
            </div>
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════
            BLUR/DESFOQUE - gradiente na parte inferior (depth of field)
        ═══════════════════════════════════════════════════════ */}
        <div 
          className="fixed bottom-0 left-0 right-0 h-48 pointer-events-none z-20"
          style={{
            background: `linear-gradient(to bottom, 
              transparent 0%, 
              ${currentTheme.deskColor}40 30%,
              ${currentTheme.deskColor}80 60%,
              ${currentTheme.deskColor}cc 80%,
              ${currentTheme.deskColor} 100%
            )`,
          }}
        />
      </div>
    </div>
  );
}
